import React, {
  forwardRef,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Box, Stack, Typography, useTheme } from '@mui/material';
import {
  CheckCircleOutline as ValidIcon,
  ErrorOutline as ErrorIcon,
  HourglassTop as CheckingIcon,
  WarningAmberOutlined as WarningIcon,
} from '@mui/icons-material';
import { alpha, type Theme } from '@mui/material/styles';
import type { IronPythonRuntimeStatus } from '../../hooks/useIronPythonValidation';
import {
  tokenizeIronPython,
  type IronPythonDiagnostic,
  type IronPythonTokenKind,
} from '../../lib/ironPythonSyntax';
import { getSemanticSurfaceTokens, layoutGap } from '../../../../theme/theme';

export interface IronPythonCodeEditorHandle {
  focusDiagnostic: (diagnostic: IronPythonDiagnostic) => void;
}

interface IronPythonCodeEditorProps {
  value: string;
  isNarrowScreen: boolean;
  wrapLines: boolean;
  diagnostics: IronPythonDiagnostic[];
  errorCount: number;
  warningCount: number;
  runtimeStatus: IronPythonRuntimeStatus;
  runtimeError?: string;
  onChange: (script: string) => void;
  onSave: () => void;
}

const GutterWidth = 48;
const editorTypography = {
  fontFamily: '"Cascadia Code", "SFMono-Regular", Consolas, monospace',
  fontSize: '0.8125rem',
  lineHeight: 1.65,
  tabSize: 4,
} as const;

const getTokenColor = (kind: IronPythonTokenKind, theme: Theme) => {
  switch (kind) {
    case 'keyword':
      return theme.palette.primary.light;
    case 'string':
      return theme.palette.success.light;
    case 'comment':
      return theme.palette.text.secondary;
    case 'number':
      return theme.palette.warning.light;
    case 'symbol':
      return theme.palette.secondary.light;
    default:
      return theme.palette.text.primary;
  }
};

const findLineOffset = (source: string, requestedLine: number) => {
  let line = 1;
  let offset = 0;
  while (line < requestedLine && offset < source.length) {
    const match = /\r\n|\r|\n/.exec(source.slice(offset));
    if (!match) {
      return source.length;
    }
    offset += (match.index ?? 0) + match[0].length;
    line += 1;
  }
  return offset;
};

const getStatusText = (
  errorCount: number,
  warningCount: number,
  runtimeStatus: IronPythonRuntimeStatus
) => {
  if (errorCount > 0) {
    return `${errorCount} ${errorCount === 1 ? 'error' : 'errors'} must be fixed`;
  }
  if (runtimeStatus === 'checking') {
    return 'Checking syntax with IronPython 3.4.2…';
  }
  if (runtimeStatus === 'unavailable') {
    return warningCount > 0
      ? `Local editor checks complete · ${warningCount} ${warningCount === 1 ? 'warning' : 'warnings'}`
      : 'Local editor checks passed';
  }
  return warningCount > 0
    ? `IronPython 3.4.2 syntax valid · ${warningCount} ${warningCount === 1 ? 'warning' : 'warnings'}`
    : 'IronPython 3.4.2 syntax valid';
};

const IronPythonCodeEditor = forwardRef<IronPythonCodeEditorHandle, IronPythonCodeEditorProps>(({
  value,
  isNarrowScreen,
  wrapLines,
  diagnostics,
  errorCount,
  warningCount,
  runtimeStatus,
  runtimeError,
  onChange,
  onSave,
}, ref) => {
  const theme = useTheme();
  const colors = getSemanticSurfaceTokens(theme);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const [scrollPosition, setScrollPosition] = useState({ left: 0, top: 0 });
  const tokenLines = useMemo(
    () => value.split(/\r\n|\r|\n/).map((line) => tokenizeIronPython(line)),
    [value]
  );
  const diagnosticLines = useMemo(
    () => new Set(diagnostics.map(({ line }) => line)),
    [diagnostics]
  );
  const hasErrors = errorCount > 0;
  const hasWarnings = warningCount > 0;
  const statusColor = hasErrors
    ? theme.palette.error.main
    : runtimeStatus === 'checking'
      ? theme.palette.info.main
      : hasWarnings || runtimeStatus === 'unavailable'
        ? theme.palette.warning.main
        : theme.palette.success.main;

  useImperativeHandle(ref, () => ({
    focusDiagnostic: (diagnostic) => {
      const editor = textareaRef.current;
      if (!editor) {
        return;
      }
      const lineOffset = findLineOffset(value, Math.max(1, diagnostic.line));
      const offset = Math.min(value.length, lineOffset + Math.max(0, (diagnostic.column ?? 1) - 1));
      editor.focus();
      editor.setSelectionRange(offset, offset);
    },
  }), [value]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
      event.preventDefault();
      onSave();
    }
  };

  return (
    <>
      <Box
        sx={{
          position: 'relative',
          flex: 1,
          minHeight: isNarrowScreen ? 300 : 'clamp(300px, 48vh, 520px)',
          overflow: 'hidden',
          backgroundColor: alpha(theme.palette.background.default, 0.54),
        }}
      >
        <Box
          aria-hidden="true"
          data-testid="iron-python-highlight-layer"
          sx={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}
        >
          <Box
            component="pre"
            data-testid="iron-python-line-numbers"
            sx={{
              ...editorTypography,
              width: wrapLines ? '100%' : 'max-content',
              minWidth: '100%',
              minHeight: '100%',
              m: 0,
              py: 2,
              whiteSpace: 'normal',
              transform: `translate(${-scrollPosition.left}px, ${-scrollPosition.top}px)`,
            }}
          >
            {tokenLines.map((tokens, lineIndex) => (
              <Box
                // Source order is the stable identity of a logical editor line.
                key={lineIndex}
                component="span"
                sx={{
                  display: 'grid',
                  gridTemplateColumns: `${GutterWidth}px minmax(0, 1fr)`,
                  width: wrapLines ? '100%' : 'max-content',
                  minWidth: '100%',
                  backgroundColor: diagnosticLines.has(lineIndex + 1)
                    ? alpha(theme.palette.warning.main, 0.045)
                    : 'transparent',
                }}
              >
                <Box
                  component="span"
                  sx={{
                    pr: 1,
                    color: colors.text.tertiary,
                    textAlign: 'right',
                    userSelect: 'none',
                    borderRight: `1px solid ${colors.border.default}`,
                    backgroundColor: alpha(theme.palette.background.paper, 0.5),
                  }}
                >
                  {lineIndex + 1}
                </Box>
                <Box
                  component="span"
                  sx={{
                    minWidth: 0,
                    px: 2,
                    whiteSpace: wrapLines ? 'pre-wrap' : 'pre',
                    overflowWrap: wrapLines ? 'anywhere' : 'normal',
                  }}
                >
                  {tokens.length === 0 && '\u200b'}
                  {tokens.map((token, tokenIndex) => (
                    <Box
                      key={`${tokenIndex}-${token.kind}`}
                      component="span"
                      sx={{
                        color: getTokenColor(token.kind, theme),
                        fontStyle: token.kind === 'comment' ? 'italic' : 'normal',
                        fontWeight: token.kind === 'keyword' ? 700 : 400,
                      }}
                    >
                      {token.value}
                    </Box>
                  ))}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
        <Box
          ref={textareaRef}
          component="textarea"
          autoFocus
          aria-label="IronPython formula code"
          aria-invalid={hasErrors}
          data-editor-engine="native"
          data-line-wrapping={wrapLines ? 'on' : 'off'}
          value={value}
          placeholder={'# Start typing, or choose "Insert starter" above.'}
          spellCheck={false}
          wrap={wrapLines ? 'soft' : 'off'}
          onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => onChange(event.target.value)}
          onKeyDown={handleKeyDown}
          onScroll={(event: React.UIEvent<HTMLTextAreaElement>) => setScrollPosition({
            left: event.currentTarget.scrollLeft,
            top: event.currentTarget.scrollTop,
          })}
          sx={{
            ...editorTypography,
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            boxSizing: 'border-box',
            m: 0,
            pt: 2,
            pr: 2,
            pb: 2,
            pl: `${GutterWidth + 16}px`,
            resize: 'none',
            overflowY: 'auto',
            overflowX: wrapLines ? 'hidden' : 'auto',
            border: 0,
            outline: 0,
            color: 'transparent',
            caretColor: colors.text.primary,
            WebkitTextFillColor: 'transparent',
            backgroundColor: 'transparent',
            '&::placeholder': {
              color: colors.text.tertiary,
              WebkitTextFillColor: colors.text.tertiary,
            },
            '&:focus-visible': {
              boxShadow: `inset 0 0 0 2px ${alpha(theme.palette.primary.main, 0.72)}`,
            },
          }}
        />
      </Box>

      <Box
        aria-label="IronPython editor syntax status"
        role="status"
        aria-live="polite"
        title={runtimeStatus === 'unavailable' ? runtimeError : undefined}
        sx={{
          px: 1.5,
          py: 1,
          borderTop: `1px solid ${colors.border.default}`,
          backgroundColor: alpha(statusColor, 0.06),
        }}
      >
        <Stack direction="row" spacing={layoutGap.comfort} alignItems="center" flexWrap="wrap">
          {hasErrors ? (
            <ErrorIcon color="error" fontSize="small" />
          ) : runtimeStatus === 'checking' ? (
            <CheckingIcon color="info" fontSize="small" />
          ) : hasWarnings || runtimeStatus === 'unavailable' ? (
            <WarningIcon color="warning" fontSize="small" />
          ) : (
            <ValidIcon color="success" fontSize="small" />
          )}
          <Typography variant="caption" fontWeight={700} sx={{ color: statusColor }}>
            {getStatusText(errorCount, warningCount, runtimeStatus)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {runtimeStatus === 'unavailable'
              ? 'IronPython syntax check unavailable; formula not executed'
              : 'Syntax check only; formula not executed in Revit'}
          </Typography>
        </Stack>
      </Box>
    </>
  );
});

IronPythonCodeEditor.displayName = 'IronPythonCodeEditor';

export default React.memo(IronPythonCodeEditor);
